import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import OpeningHoursEditor from './components/OpeningHoursEditor';
import EventsEditor from './components/EventsEditor';
import MenuEditor from './components/MenuEditor';
import DatabaseMigration from './components/DatabaseMigration';
import styles from './AdminDashboard.module.css';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const { hotelData, loading: dataLoading, usingDatabase } = useData();
  const [activeSection, setActiveSection] = useState('overview');

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'hours', label: 'Opening Hours', icon: '🕐' },
    { id: 'events', label: 'Events', icon: '📅' },
    { id: 'menu', label: 'Menu Items', icon: '🍽️' },
    { id: 'migration', label: 'Database Migration', icon: '🗄️' },
    { id: 'content', label: 'Content Management', icon: '📝' }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className={styles['content-section']}>
            <h2>Dashboard Overview</h2>
            <div className={styles['stats-grid']}>
              <div className={styles['stat-card']}>
                <h3>Data Source</h3>
                <p className={styles['stat-value']}>
                  {usingDatabase ? '🗄️ Database' : '📄 JSON File'}
                </p>
                <span className={styles['stat-label']}>
                  {usingDatabase ? 'Connected to Supabase' : 'Using local JSON file'}
                </span>
              </div>
              <div className={styles['stat-card']}>
                <h3>Current Events</h3>
                <p className={styles['stat-value']}>
                  {dataLoading ? '...' : hotelData?.events?.filter(e => e.active)?.length || 0}
                </p>
                <span className={styles['stat-label']}>
                  {hotelData?.events?.filter(e => e.active)?.map(e => e.title).join(', ') || 'Loading...'}
                </span>
              </div>
              <div className={styles['stat-card']}>
                <h3>Menu Items</h3>
                <p className={styles['stat-value']}>
                  {dataLoading ? '...' : (
                    (hotelData?.menuItems?.starters?.length || 0) +
                    (hotelData?.menuItems?.pubClassics?.length || 0) +
                    (hotelData?.menuItems?.curries?.length || 0) +
                    (hotelData?.menuItems?.fromTheSea?.length || 0) +
                    (hotelData?.menuItems?.burgers?.length || 0) +
                    (hotelData?.menuItems?.desserts?.length || 0) +
                    (hotelData?.menuItems?.sides?.length || 0) +
                    (hotelData?.menuItems?.drinks?.length || 0)
                  )}
                </p>
                <span className={styles['stat-label']}>Total items</span>
              </div>
            </div>
            <div className={styles['recent-activity']}>
              <h3>Recent Activity</h3>
              <p>No recent changes to display.</p>
            </div>
          </div>
        );
      
      case 'hours':
        return <OpeningHoursEditor />;
      
      case 'events':
        return <EventsEditor />;
      
      case 'menu':
        return <MenuEditor />;
      
      case 'migration':
        return <DatabaseMigration />;
      
      case 'content':
        return (
          <div className={styles['content-section']}>
            <h2>Content Management</h2>
            <p>Manage page content, images, and descriptions.</p>
            <div className={styles['coming-soon']}>
              <p>🚧 Coming Soon - Content Editor</p>
            </div>
          </div>
        );
      
      default:
        return <div>Select a section from the menu</div>;
    }
  };

  return (
    <div className={styles['admin-dashboard']}>
      {/* Sidebar */}
      <div className={styles['sidebar']}>
        <div className={styles['sidebar-header']}>
          <h1 className={styles['admin-title']}>QH Admin</h1>
          <p className={styles['admin-subtitle']}>Management Portal</p>
        </div>
        
        <nav className={styles['admin-nav']}>
          {menuItems.map(item => (
            <button
              key={item.id}
              className={`${styles['nav-item']} ${activeSection === item.id ? styles.active : ''}`}
              onClick={() => setActiveSection(item.id)}
            >
              <span className={styles['nav-icon']}>{item.icon}</span>
              <span className={styles['nav-label']}>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className={styles['sidebar-footer']}>
          <button
            className={styles['logout-button']}
            onClick={handleLogout}
          >
            <span className={styles['logout-icon']}>🚪</span>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles['main-content']}>
        <header className={styles['content-header']}>
          <h1 className={styles['page-title']}>
            {menuItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
          </h1>
          <div className={styles['header-actions']}>
            <span className={styles['user-info']}>Logged in as Admin</span>
          </div>
        </header>

        <main className={styles['content-body']}>
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;